/**
 * React Query keys and functions for concept architecture data
 *
 * This file centralizes all query keys and API functions for React Query.
 * Using query key factories ensures consistent cache keys across the app.
 */

import type {
	Concept,
	ConceptAssignmentInput,
	Formation,
	PresetRoute,
	RoleTerminology,
} from '@/types/Concept.types.ts'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'

// ============================================================================
// QUERY KEY FACTORIES
// ============================================================================

export const conceptKeys = {
	all: ['concepts'] as const,
	formations: (playbookId: string) => ['concepts', 'formations', playbookId] as const,
	formation: (id: string) => ['concepts', 'formation', id] as const,
	baseConcepts: (playbookId: string) =>
		['concepts', 'base', playbookId] as const,
	baseConcept: (id: string) => ['concepts', 'base', id] as const,
	presetRoutes: () => ['concepts', 'preset-routes'] as const,
	roles: (playbookId: string) => ['concepts', 'roles', playbookId] as const,
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		if (response.status === 401) {
			// Let the caller handle auth redirects
			throw new Error('UNAUTHORIZED')
		}
		const error = await response.text()
		throw new Error(error || `HTTP ${response.status}`)
	}
	return response.json()
}

// ----------------------------------------------------------------------------
// FORMATIONS (block-based API)
// ----------------------------------------------------------------------------

export async function fetchFormations(playbookId: string): Promise<Formation[]> {
	const response = await secureFetch(`/api/playbooks/${playbookId}/formation-blocks`)
	const data = await handleResponse<{ items: Formation[] }>(response)
	return data.items
}

export async function fetchFormation(id: string): Promise<Formation> {
	const response = await secureFetch(`/api/formation-blocks/${id}`)
	return handleResponse<Formation>(response)
}

export async function fetchFormationsPage(
	playbookId: string,
	limit: number,
	cursor?: string,
): Promise<{ items: Formation[]; nextCursor?: string | null }> {
	const params = new URLSearchParams({ limit: String(limit) })
	if (cursor) params.set('cursor', cursor)
	const response = await secureFetch(`/api/playbooks/${playbookId}/formation-blocks?${params}`)
	return handleResponse<{ items: Formation[]; nextCursor?: string | null }>(response)
}

export async function createFormation(
	playbookId: string,
	data: {
		name: string
		description?: string
		hashMode?: boolean
		thumbnail?: string | null
		positions: Array<{
			role: string
			x: number
			y: number
			color?: string
			isLineman?: boolean
			hashRelative?: boolean
			hashMark?: string | null
		}>
	},
): Promise<Formation> {
	const response = await secureJsonFetch(`/api/playbooks/${playbookId}/formation-blocks`, {
		method: 'POST',
		body: data,
	})
	const result = await handleResponse<{ formation: Formation }>(response)
	return result.formation
}

export async function updateFormation(
	id: string,
	data: Partial<Formation>,
): Promise<Formation> {
	const response = await secureJsonFetch(`/api/formation-blocks/${id}`, {
		method: 'PUT',
		body: data,
	})
	return handleResponse<Formation>(response)
}

export async function deleteFormation(id: string): Promise<void> {
	const response = await secureFetch(`/api/formation-blocks/${id}`, {
		method: 'DELETE',
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to delete formation: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}

// ----------------------------------------------------------------------------
// BASE CONCEPTS
// ----------------------------------------------------------------------------

export async function fetchConcepts(
	playbookId: string,
): Promise<Concept[]> {
	const response = await secureFetch(`/api/playbooks/${playbookId}/concepts`)
	const data = await handleResponse<{ concepts: Concept[] }>(response)
	return data.concepts
}

export async function fetchConceptsPage(
	playbookId: string,
	limit: number,
	cursor?: string,
): Promise<{ concepts: Concept[]; nextCursor?: string; hasMore: boolean }> {
	const params = new URLSearchParams({ limit: String(limit) })
	if (cursor) params.set('cursor', cursor)
	const response = await secureFetch(`/api/playbooks/${playbookId}/concepts-page?${params}`)
	return handleResponse(response)
}

export async function createConcept(
	playbookId: string,
	data: {
		name: string
		description?: string
		targeting_mode: 'absolute_role' | 'relative_selector'
		ball_position?: 'left' | 'center' | 'right'
		play_direction?: 'left' | 'right' | 'na'
		assignments: ConceptAssignmentInput[]
	},
): Promise<Concept> {
	const response = await secureJsonFetch(`/api/playbooks/${playbookId}/concepts`, {
		method: 'POST',
		body: data,
	})
	return handleResponse(response)
}

export async function updateConcept(
	id: string,
	data: Partial<Concept>,
): Promise<Concept> {
	const response = await secureJsonFetch(`/api/concepts/${id}`, {
		method: 'PUT',
		body: data,
	})
	return handleResponse(response)
}

export async function deleteConcept(id: string): Promise<void> {
	const response = await secureFetch(`/api/concepts/${id}`, {
		method: 'DELETE',
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to delete concept: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}

// ----------------------------------------------------------------------------
// PRESET ROUTES & ROLES
// ----------------------------------------------------------------------------

export async function fetchPresetRoutes(): Promise<PresetRoute[]> {
	const response = await secureFetch('/api/preset-routes')
	const data = await handleResponse<{ routes: PresetRoute[] }>(response)
	return data.routes
}

export async function fetchRoles(playbookId: string): Promise<RoleTerminology[]> {
	const response = await secureFetch(`/api/playbooks/${playbookId}/roles`)
	const data = await handleResponse<{ roles: RoleTerminology[] }>(response)
	return data.roles
}

export async function updateRoles(
	playbookId: string,
	roles: RoleTerminology[],
): Promise<RoleTerminology[]> {
	const response = await secureJsonFetch(`/api/playbooks/${playbookId}/roles`, {
		method: 'PUT',
		body: { roles },
	})
	const data = await handleResponse<{ roles: RoleTerminology[] }>(response)
	return data.roles
}
